import {
  Box,
  Button,
  Flex,
  Input,
  ListItem,
  Text,
  ValidationMessage,
} from '@modusign/design-system';
import PropTypes from 'prop-types';
import PropsType from 'props-type';
import { ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isMobilePhone from 'validator/lib/isMobilePhone';

import useBulkRequestItem from '@app/features/bulkRequest/hooks/useBulkRequestItem';
import useSendTestBulkRequest from '@app/features/bulkRequest/hooks/useSendTestBulkRequest';
import { validateEmail } from '@app/libs/validation';
import { getTaskNumber } from '@app/store/bulkRequest/requestTasks/tasks/selectors';
import { getColumnValue } from '@app/store/bulkRequest/selectors';
import { modalClose } from '@app/store/modal/actions';
import { RootState } from '@app/store/types';

import './BulkRequestSendTestModal.scss';

const propTypes = {
  bulkRequestId: PropTypes.string.isRequired,
  taskId: PropTypes.string.isRequired,
};

type BulkRequestSendTestModalProps = PropsType<typeof propTypes>;

function BulkRequestSendTestModal({ bulkRequestId, taskId }: BulkRequestSendTestModalProps) {
  const dispatch = useDispatch();
  const [contact, setContact] = useState('');
  const [contactError, setContactError] = useState('');

  const taskNumber = useSelector((state: RootState) => getTaskNumber(state, { id: taskId }));
  const title = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'DOCUMENT_TITLE' })
  );

  const { sendTestBulkRequest } = useSendTestBulkRequest(taskId);
  const { data: bulkRequest } = useBulkRequestItem(bulkRequestId);

  function validateContact(value: string) {
    // eslint-disable-next-line camelcase
    if (!validateEmail(value).success && !isMobilePhone(value, 'ko-KR')) {
      setContactError('올바른 이메일 주소 또는 휴대폰 번호를 입력해주세요.');
      return false;
    }
    setContactError('');
    return true;
  }

  function handleChangeContact(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setContact(value);
    validateContact(value);
  }

  async function handleSubmit() {
    if (validateContact(contact) && bulkRequest) {
      dispatch(modalClose());
      await sendTestBulkRequest({
        contact: validateEmail(contact).success ? contact : contact.replace(/-/g, ''),
        signingDuration: bulkRequest.signingDuration,
      });
    }
  }

  const onCancelTestContact = () => {
    dispatch(modalClose());
  };

  return (
    <Box className="Container Container--max-xs">
      <Box className="Card Card--pop box-6x align-center">
        <Box mb={7}>
          <Text as="p" fontSize="22px" fontWeight="bold">
            테스트 전송하기
          </Text>
        </Box>
        <Box mb={7}>
          <Text as="p" fontSize="14px" fontWeight="medium" mb={1}>
            {`${taskNumber}번 "${title}"으로/로 서명 요청을 테스트합니다.`}
          </Text>
          <Text as="p">테스트 전송할 연락처를 입력해 주세요.</Text>
        </Box>

        <Box mb={6}>
          <Input
            isInvalid={!!contactError}
            placeholder="이메일 또는 휴대폰 번호"
            type="text"
            value={contact}
            onBlur={handleChangeContact}
            onChange={handleChangeContact}
          />
          <ValidationMessage textAlign="start">{contactError}</ValidationMessage>
        </Box>

        <Flex
          alignItems="start"
          backgroundColor="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          flexDirection="column"
          mb={9}
          px={5}
          py={7}
        >
          <Text as="p" fontWeight="medium" mb={2} textAlign="start">
            테스트 전송할 경우
          </Text>
          <Flex as="ul" flexDirection="column" gap={2} textAlign="start">
            <ListItem>
              <Text fontSize="sm">엑셀 양식에 입력한 참조자에게는 알림이 전송되지 않습니다.</Text>
            </ListItem>
            <ListItem>
              <Text fontSize="sm">템플릿에서 설정한 첨부파일이 전송되지 않습니다.</Text>
            </ListItem>
          </Flex>
        </Flex>

        <Flex flexDirection="row" gap={3} justifyContent="center" mb={6}>
          <Button onClick={onCancelTestContact}>취소</Button>
          <Button
            data-testid="bulk-request-test-send-button"
            disabled={!!contactError || !taskId}
            variant="primary"
            onClick={handleSubmit}
          >
            테스트 전송하기
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}

export default BulkRequestSendTestModal;
